import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { findValueInEnum, maybe, parseBoolean } from "@saleor/misc";
import { RemittanceRunnerListUrlQueryParams } from "@saleor/remittanceRunner/url";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import {
  CourierTypeEnum,
  DeliveryTypeEnum,
  RemittanceStatusEnum
} from "@saleor/types/globalTypes";
import { dedupeFilter } from "@saleor/utils/filters";

import { RemittanceRunnerMallFilterData_malls_edges_node } from "../types/RemittanceRunnerMallFilterData";
import { RemittanceRunnerFilterOpts } from "./filters";

export function getFilterOptsForNonDispatcher(
  params: RemittanceRunnerListUrlQueryParams,
  malls: {
    initial: RemittanceRunnerMallFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): RemittanceRunnerFilterOpts {
  return {
    companyName: {
      active: maybe(() => params.companyName !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.companyName.map(name =>
              findValueInEnum(name, CourierTypeEnum)
            )
          ),
        []
      )
    },
    hasReceipt: {
      active: params.hasReceipt !== undefined,
      value: maybe(() => parseBoolean(params.hasReceipt, true))
    },
    isPaymentCollected: {
      active: params.isPaymentCollected !== undefined,
      value: maybe(() => parseBoolean(params.isPaymentCollected, true))
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: params?.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    motherOrder: {
      active: !!maybe(() => params.motherOrder),
      value: params.motherOrder
    },
    remittanceDate: {
      active: maybe(
        () =>
          [params.remittanceDateFrom, params.remittanceDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.remittanceDateTo, ""),
        min: maybe(() => params.remittanceDateFrom, "")
      }
    },
    remittanceId: {
      active: !!maybe(() => params.remittanceId),
      value: params.remittanceId
    },
    remitterName: {
      active:
        !!maybe(() => params.remitterFirstName) ||
        !!maybe(() => params.remitterLastName),
      value: {
        firstName: params.remitterFirstName,
        lastName: params.remitterLastName
      }
    },
    search: {
      active: maybe(
        () => [params.query].some(field => field !== undefined),
        false
      ),
      value: maybe(() => params.query, "")
    },
    status: {
      active: maybe(() => params.status !== undefined, false),
      value: maybe(() => findValueInEnum(params.status, RemittanceStatusEnum))
    }
  };
}

export function getFilterOptsForDispatcher(
  params: RemittanceRunnerListUrlQueryParams
): RemittanceRunnerFilterOpts {
  return {
    createdDate: {
      active: maybe(
        () =>
          [params.createdDateFrom, params.createdDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.createdDateTo, ""),
        min: maybe(() => params.createdDateFrom, "")
      }
    },
    dispatcherName: {
      active:
        !!maybe(() => params.dispatcherFirstName) ||
        !!maybe(() => params.dispatcherLastName),
      value: {
        firstName: params.dispatcherFirstName,
        lastName: params.dispatcherLastName
      }
    },
    hasReceipt: {
      active: params.hasReceipt !== undefined,
      value: maybe(() => parseBoolean(params.hasReceipt, true))
    },
    remittanceDate: {
      active: maybe(
        () =>
          [params.remittanceDateFrom, params.remittanceDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.remittanceDateTo, ""),
        min: maybe(() => params.remittanceDateFrom, "")
      }
    },
    remittanceId: {
      active: !!maybe(() => params.remittanceId),
      value: params.remittanceId
    },
    search: {
      active: maybe(
        () => [params.query].some(field => field !== undefined),
        false
      ),
      value: maybe(() => params.query, "")
    },
    status: {
      active: maybe(() => params.status !== undefined, false),
      value: maybe(() => findValueInEnum(params.status, RemittanceStatusEnum))
    }
  };
}

export function getFilterOptsForToRemit(
  params: RemittanceRunnerListUrlQueryParams
): RemittanceRunnerFilterOpts {
  return {
    amount: {
      active: !!maybe(() => params.amount),
      value: params.amount
    },
    dateCompleted: {
      active: maybe(
        () =>
          [params.dateCompletedFrom, params.dateCompletedTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.dateCompletedTo, ""),
        min: maybe(() => params.dateCompletedFrom, "")
      }
    },
    dateShipped: {
      active: maybe(
        () =>
          [params.dateShippedFrom, params.dateShippedTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.dateShippedTo, ""),
        min: maybe(() => params.dateShippedFrom, "")
      }
    },
    deliveryType: {
      active: maybe(() => params.deliveryType !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.deliveryType.map(status =>
              findValueInEnum(status, DeliveryTypeEnum)
            )
          ),
        []
      )
    },
    motherOrder: {
      active: !!maybe(() => params.motherOrder),
      value: params.motherOrder
    },
    riderName: {
      active:
        !!maybe(() => params.riderFirstName) ||
        !!maybe(() => params.riderLastName),
      value: {
        firstName: params.riderFirstName,
        lastName: params.riderLastName
      }
    },
    search: {
      active: maybe(
        () => [params.query].some(field => field !== undefined),
        false
      ),
      value: maybe(() => params.query, "")
    }
  };
}
